import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { MessageSquare } from 'lucide-react';
import { RootState } from '../store';
import SpeechRateModal from './SpeechRateModal';
import SessionFeedbackModal from './SessionFeedbackModal';
import { getShortClientId } from '../utils/clientId';
import { config } from '../config';
import { apiService } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';

interface TopBarProps {
  onSignOut: () => void;
  isSaving?: boolean;
  clientId?: string;
  roomId?: string | null;
}

const TopBar: React.FC<TopBarProps> = ({ onSignOut, isSaving = false, clientId = '', roomId }) => {
  const { isConnected } = useSelector((state: RootState) => state.connection);
  const { showToast } = useToast();
  const { stats, currentSpeechRate } = useSelector((state: RootState) => state.conversation);
  const [showSpeechModal, setShowSpeechModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const handleFeedbackSubmit = async (formData: FormData) => {
    if (!roomId) return;
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      await apiService.submitSessionFeedback(roomId, formData);
      setShowFeedbackModal(false);
      showToast('Session feedback submitted');
    } catch (err: any) {
      setFeedbackError(err?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Extract port from WebSocket URL
  const getServerPort = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.port || '80';
    } catch {
      return 'Unknown';
    }
  };

  const getSpeechRateDisplay = () => {
    if (currentSpeechRate.words_per_minute === 0) {
      return '0 wpm - No speech';
    }
    if (currentSpeechRate.is_too_fast) {
      return `${currentSpeechRate.words_per_minute} wpm - Fast`;
    }
    return `${currentSpeechRate.words_per_minute} wpm - Normal`;
  };

  const getSpeechRateColor = () => {
    if (currentSpeechRate.is_too_fast) {
      return 'bg-red-500';
    }
    return currentSpeechRate.words_per_minute > 0 ? 'bg-green-500' : 'bg-gray-500';
  };

  return (
    <>
      <div className="bg-blue-500 text-white px-4 py-1 shadow-lg">
        <div className="flex items-center justify-between">
          {/* Left side - App title and client/server status */}
          <div className="flex items-center space-x-3">
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-bold">💬 {config.app.name}</span>
              <span className="text-xs opacity-75">Powered by Apexia Labs</span>
            </div>
            {clientId && (
              <div className="flex items-center bg-blue-600 px-2 py-0.5 rounded-full text-xs">
                <span>🆔 {getShortClientId(clientId)}</span>
              </div>
            )}
          </div>

          {/* Right side - Stats and controls */}
          <div className="flex items-center space-x-2">
            {/* Questions counter */}
            {/* <div className="flex items-center space-x-2 bg-blue-600 px-3 py-1 rounded-full text-sm">
              <span>❓ Questions: {stats.questionsDetected}</span>
            </div> */}

            {/* Pending counter */}
            {/* <div className="flex items-center space-x-2 bg-orange-500 px-3 py-1 rounded-full text-sm">
              <span>⏳ Pending: {stats.pendingAnswers}</span>
            </div> */}

            {/* Speech rate display */}
            <button
              onClick={() => setShowSpeechModal(true)}
              className={`flex items-center space-x-1 ${getSpeechRateColor()} px-2 py-0.5 rounded-full text-xs hover:opacity-80 transition-opacity`}
            >
              <span>🗣️ {getSpeechRateDisplay()}</span>
            </button>

            {/* Session feedback button */}
            <button
              onClick={() => setShowFeedbackModal(true)}
              disabled={!roomId}
              title={roomId ? 'Send session feedback' : 'No active session'}
              className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed px-2 py-0.5 rounded text-xs transition-colors"
            >
              <MessageSquare className="w-3 h-3" />
              <span>Feedback</span>
            </button>

            {/* Sign Out button */}
            <button
              onClick={onSignOut}
              disabled={isSaving}
              className={`${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'} px-2 py-0.5 rounded text-xs font-medium transition-colors flex items-center space-x-1`}
            >
              {isSaving ? (
                <>
                  <span className="animate-spin">⏳</span>
                  <span>Saving...</span>
                </>
              ) : (
                <span>Sign Out</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Speech Rate Modal */}
      <SpeechRateModal
        isOpen={showSpeechModal}
        onClose={() => setShowSpeechModal(false)}
      />

      {/* Session Feedback Modal */}
      <SessionFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => {
          setShowFeedbackModal(false);
          setFeedbackError(null);
        }}
        onSubmit={handleFeedbackSubmit}
        submitting={feedbackSubmitting}
        error={feedbackError}
      />
    </>
  );
};

export default TopBar;